/**
 * 메모 본문 내 찾기 — 순수 계산부 (2026-08-18).
 *
 * 편집 화면은 평범한 `<textarea>` 라 "몇 번째 글자가 화면 어디에 있는지" 를 알려주지 않는다.
 * 그래서 두 가지를 여기서 따로 만든다.
 *
 *   1) `findMatches` — 대소문자 무시 일치 목록. 정규식이 아니라 `indexOf` 다.
 *      사용자가 `(`·`*` 같은 글자를 그대로 치는 검색창이라 이스케이프 부담을 지지 않는다.
 *   2) `scrollToOffset` — 오프셋의 y 좌표를 **미러 요소**로 재서 스크롤한다.
 *      `setSelectionRange` 는 스크롤을 시키지 않는다(크로미움 실측 확인, 2026-08-18).
 */

export interface MemoMatch {
  start: number;
  end: number;
}

/**
 * 하이라이트 상한 — 한 글자 검색처럼 일치가 폭증하면 오버레이 노드도 같이 폭증한다.
 * 본문 상한이 20,000자라 500개면 실사용에서 잘릴 일이 거의 없다.
 */
export const MEMO_FIND_MAX_MATCHES = 500;

/**
 * 대소문자 무시 · 겹치지 않는 전체 일치.
 *
 * ⚠ `toLowerCase()` 는 글자 수가 변할 수 있다(예: `İ` → 2글자). 그러면 소문자 문자열에서 찾은
 * 인덱스가 원본 오프셋과 어긋나 엉뚱한 자리를 선택하게 된다. 길이가 달라지면 원본 그대로
 * 대소문자를 구분해 찾는 쪽으로 물러선다 — 틀린 자리로 보내는 것보다 낫다.
 */
export function findMatches(body: string, query: string): MemoMatch[] {
  const needleRaw = query.trim();
  if (needleRaw === '' || body === '') return [];

  const lowerBody = body.toLowerCase();
  const lowerNeedle = needleRaw.toLowerCase();
  const safeToFold = lowerBody.length === body.length && lowerNeedle.length === needleRaw.length;
  const hay = safeToFold ? lowerBody : body;
  const needle = safeToFold ? lowerNeedle : needleRaw;

  const out: MemoMatch[] = [];
  let from = 0;
  while (out.length < MEMO_FIND_MAX_MATCHES) {
    const at = hay.indexOf(needle, from);
    if (at < 0) break;
    out.push({ start: at, end: at + needle.length });
    from = at + needle.length;
  }
  return out;
}

/** 미러 요소가 원본 textarea 와 **같은 줄바꿈**을 하도록 옮겨야 하는 속성들. */
const MIRROR_PROPS = [
  'font-family', 'font-size', 'font-weight', 'font-style', 'font-variant',
  'letter-spacing', 'line-height', 'text-indent', 'text-transform',
  'word-spacing', 'tab-size',
  'padding-top', 'padding-right', 'padding-bottom', 'padding-left',
] as const;

/**
 * 본문 오프셋의 y 좌표(스크롤 콘텐츠 기준, px). 잴 수 없으면 null.
 *
 * 원리: textarea 와 글꼴·폭·여백이 같은 숨은 div 에 **오프셋 앞까지의 글**만 넣고, 그 끝에
 * 표식을 하나 붙여 표식의 offsetTop 을 읽는다. 줄바꿈 규칙이 같으므로 표식은 실제 그 글자가
 * 그려질 줄의 맨 위에 온다.
 *
 * ⚠ 폭은 `clientWidth` 다 — 세로 스크롤바가 차지한 폭을 뺀 값이라야 줄바꿈이 원본과 같아진다.
 */
export function measureOffsetTop(ta: HTMLTextAreaElement, index: number): number | null {
  if (typeof document === 'undefined' || typeof getComputedStyle !== 'function') return null;
  const width = ta.clientWidth;
  if (!Number.isFinite(width) || width <= 0) return null;

  const cs = getComputedStyle(ta);
  const mirror = document.createElement('div');
  for (const prop of MIRROR_PROPS) mirror.style.setProperty(prop, cs.getPropertyValue(prop));
  mirror.style.setProperty('box-sizing', 'border-box');
  mirror.style.setProperty('width', `${width}px`);
  mirror.style.setProperty('position', 'absolute');
  mirror.style.setProperty('top', '0');
  mirror.style.setProperty('left', '-9999px');
  mirror.style.setProperty('visibility', 'hidden');
  mirror.style.setProperty('pointer-events', 'none');
  mirror.style.setProperty('white-space', 'pre-wrap');
  mirror.style.setProperty('overflow-wrap', 'break-word');

  mirror.textContent = ta.value.slice(0, index);
  const marker = document.createElement('span');
  marker.textContent = '​';
  mirror.appendChild(marker);

  document.body.appendChild(mirror);
  const top = marker.offsetTop;
  document.body.removeChild(mirror);
  return Number.isFinite(top) ? top : null;
}

/**
 * 해당 오프셋이 보이도록 본문을 스크롤한다.
 * 이미 편안히 보이는 자리면 움직이지 않는다 — 다음/이전을 눌러도 화면이 매번 튀지 않게.
 */
export function scrollToOffset(ta: HTMLTextAreaElement, index: number): void {
  const top = measureOffsetTop(ta, index);
  if (top == null) return;
  const view = ta.clientHeight;
  const cur = ta.scrollTop;
  if (top >= cur + 8 && top <= cur + view - 28) return;
  ta.scrollTop = Math.max(0, Math.round(top - view / 3));
}
